/**
 * The digit mode, shared by everything that renders extracted numbers.
 *
 * **Neither mode converts anything.** "printed" renders `value_raw`,
 * "western" renders `value_western`; both are strings the pipeline stored.
 * Nothing here transliterates at render time -- doing that is exactly the
 * operation that would hide a transliteration error.
 *
 * The default is `western`. The plan argued for `printed` on the grounds
 * that checking a figure against the image is easier in the form the page
 * prints, which is true; but Latin digits are what this reader wants by
 * default, and the transliteration is not a guess -- every one of the
 * 57,135 facts and 130,795 table cells in this database has a stored western
 * twin, so the choice costs no fidelity. `d` swaps it, and the choice
 * persists.
 */
import {
  createContext, use, useCallback, useState, type ReactNode,
} from 'react'

export type DigitMode = 'printed' | 'western'

export const DEFAULT_DIGIT_MODE: DigitMode = 'western'

const KEY = 'explorer.digits'

export function readDigitMode(): DigitMode {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'printed' || v === 'western') return v
  } catch {}
  return DEFAULT_DIGIT_MODE
}

export function storeDigitMode(mode: DigitMode): void {
  try { localStorage.setItem(KEY, mode) } catch {}
}

/** The mode plus a toggle, persisted, for screens that own the control. */
export function useDigitMode(): [DigitMode, () => void, (m: DigitMode) => void] {
  const [mode, setMode] = useState<DigitMode>(readDigitMode)
  const set = useCallback((m: DigitMode) => { storeDigitMode(m); setMode(m) }, [])
  const toggle = useCallback(
    () => set(mode === 'printed' ? 'western' : 'printed'),
    [mode, set],
  )
  return [mode, toggle, set]
}

const Ctx = createContext<DigitMode>(DEFAULT_DIGIT_MODE)

export const DigitProvider = ({ mode, children }: { mode: DigitMode; children: ReactNode }) =>
  <Ctx value={mode}>{children}</Ctx>

export const useDigits = () => use(Ctx)

/**
 * Pick the string to show. When the requested one is absent the other is
 * shown rather than a dash -- a missing transliteration is a pipeline fact
 * worth seeing, not a reason to render nothing.
 */
export function pickDigits(
  mode: DigitMode,
  raw: string | null,
  western: string | null,
): { text: string | null; isFallback: boolean } {
  const want = mode === 'printed' ? raw : western
  const other = mode === 'printed' ? western : raw
  if (want !== null && want !== '') return { text: want, isFallback: false }
  if (other !== null && other !== '') return { text: other, isFallback: true }
  return { text: null, isFallback: false }
}
